import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import DatePicker from "@components/DatePicker";
import {
  ClockIcon,
  DropdownArrow,
  TimeRangeButton,
  TimeRangeDropdownWrap,
} from "./styles";

/**
 * The Tracks-screen day picker, restyled for the dark metrics panels.
 *
 * Wraps the shared `@components/DatePicker` rather than reimplementing it, so
 * the dot rendering, locale registration and week-start stay in one place. Only
 * the palette is overridden here — react-datepicker's stylesheet is global and
 * light-themed, so the overrides are scoped under this wrapper and never leak
 * back to the Tracks screen.
 */
const Wrap = styled.div`
  .react-datepicker {
    background: #16181d;
    border: 1px solid #2a2e37;
    border-radius: 8px;
    font-family: Nunito Sans, sans-serif;
  }
  .react-datepicker__header {
    background: #1b1e24;
    border-bottom: 1px solid #2a2e37;
    padding-top: 10px;
  }
  .react-datepicker__current-month {
    color: #e9edf2;
    font-size: 13px;
    font-weight: 700;
  }
  .react-datepicker__day-name {
    color: #7b8794;
    font-size: 11px;
  }
  /* The stylesheet greys out weekend NAMES; in a dark panel that reads as
     disabled rather than as the weekend, so bring them back to the same tone. */
  .react-datepicker__day-name:nth-of-type(6),
  .react-datepicker__day-name:nth-of-type(7) {
    color: #7b8794;
  }
  .react-datepicker__day {
    color: #c7d0d9;
    border-radius: 4px;
  }
  .react-datepicker__day:hover {
    background: #262b35;
    color: #e9edf2;
  }
  .react-datepicker__day--today {
    font-weight: 700;
    color: #eab839;
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: #2f6f3e;
    color: #ffffff;
  }
  .react-datepicker__day--selected:hover {
    background: #3a8a4d;
  }
  /* Days before the device's first stored point, or in the future. They must
     read as unavailable, not merely dim — the whole reason they are disabled is
     that the rollup knows about days the raw points no longer cover. */
  .react-datepicker__day--disabled {
    color: #454b55;
    cursor: not-allowed;
  }
  .react-datepicker__day--disabled:hover {
    background: transparent;
    color: #454b55;
  }
  .react-datepicker__day--outside-month {
    color: #4d545e;
  }
  .react-datepicker__navigation-icon::before {
    border-color: #7b8794;
  }
  .react-datepicker__navigation:hover *::before {
    border-color: #e9edf2;
  }
  /* The data dot. Green so it reads as "there is something here", and nudged
     clear of the digit so it survives the smaller dark-panel day cells. */
  .day-data-dot {
    background: #73bf69;
    top: 1px;
    right: 1px;
  }
  .react-datepicker__day--selected .day-data-dot {
    background: #ffffff;
  }
`;

export const DarkCalendar: React.FC<{
  selected: Date | null;
  onChange: (d: Date | null) => void;
  datesWithData: Date[];
  minDate?: Date;
  maxDate?: Date;
  onMonthChange?: (d: Date) => void;
}> = ({ selected, onChange, datesWithData, minDate, maxDate, onMonthChange }) => (
  <Wrap>
    <DatePicker
      selected={selected}
      onChange={onChange}
      datesWithData={datesWithData}
      minDate={minDate}
      maxDate={maxDate}
      onMonthChange={onMonthChange}
    />
  </Wrap>
);

/**
 * The calendar as a dropdown, for sitting in a toolbar rather than a side pane.
 *
 * The panel is absolutely positioned and z-indexed above the charts, so opening
 * it covers them instead of reflowing the page — a calendar that pushed the
 * plots down would move the thing the reader is comparing against.
 *
 * Interaction deliberately mirrors TimeRangeDropdown (same button, same
 * click-outside close): they sit side by side and are two ways of answering the
 * same question, so behaving differently would be the surprise.
 */
const Popover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 100;
  background: #1a202c;
  border: 1px solid #4a5568;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  padding: 8px;
`;

const PopoverHint = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #6b7785;
  padding: 6px 4px 2px;
  max-width: 232px;
`;

export const CalendarDropdown: React.FC<{
  selected: Date | null;
  onChange: (d: Date | null) => void;
  datesWithData: Date[];
  minDate?: Date;
  maxDate?: Date;
  onMonthChange?: (d: Date) => void;
  /** Button text when no day is picked. */
  placeholder: string;
  hint?: string;
}> = ({ placeholder, hint, onChange, ...rest }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = rest.selected
    ? rest.selected.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : placeholder;

  return (
    <TimeRangeDropdownWrap ref={wrapRef} style={{ marginLeft: 0 }}>
      <TimeRangeButton active={open} onClick={() => setOpen(!open)}>
        <ClockIcon>&#128197;</ClockIcon>
        {label}
        <DropdownArrow>&#9662;</DropdownArrow>
      </TimeRangeButton>
      {open && (
        <Popover>
          <DarkCalendar
            {...rest}
            onChange={(d) => {
              onChange(d);
              // Close on pick: the day is chosen, and leaving it open would keep
              // the charts it just changed hidden behind it.
              setOpen(false);
            }}
          />
          {hint ? <PopoverHint>{hint}</PopoverHint> : null}
        </Popover>
      )}
    </TimeRangeDropdownWrap>
  );
};
